import { AlertCircle, Check, Info, X } from "lucide-react";

import { useJDQuestions } from "@/api/jd-question-queries";
import { useMoveJD, type JDDetail } from "@/api/jd-queries";
import { TryAPick } from "@/components/jd-try-pick";
import { Button } from "@/components/ui/button";
import { ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";
import { audienceOf, checklist, publishBlock, STEP_TITLE, type StepKey } from "@/lib/jd-steps";
import { cn, formatDate } from "@/lib/utils";

/**
 * Step 5, Preview & publish — DESIGN §20.8, §20.9. The readiness strip became a checklist: what blocks
 * publishing and what only warns, each with the step that fixes it. Beside it, a pick made the way
 * ipp-platform makes one (`POST /jds/{id}/preview` runs the same code as `/pick`), so what a member
 * would be asked can be seen before anyone is.
 */

const MARK = {
  ok: { icon: Check, className: "bg-bank-good-wash text-bank-good-ink" },
  warn: { icon: AlertCircle, className: "bg-bank-attention-wash text-bank-attention-ink" },
  // Attention, not the failure red: a blocker is somebody's job, which is what attention means (BRANDING §4).
  block: { icon: X, className: "bg-bank-attention text-bank-on-accent" },
} as const;

function Checklist({ jd, onGo }: { jd: JDDetail; onGo: (step: StepKey) => void }) {
  const questions = useJDQuestions(jd.jd.id);
  const items = checklist(jd, questions.data);
  return (
    <Panel>
      <PanelHeader title={jd.jd.status === "active" ? "Readiness" : "Before you publish"} />
      <ul>
        {items.map((item) => {
          const mark = MARK[item.tone];
          return (
            <li
              key={item.title}
              className="border-bank-border flex items-start gap-2.5 border-t px-4 py-2.5 first:border-t-0"
            >
              <span
                className={cn("grid size-[22px] shrink-0 place-items-center rounded-full", mark.className)}
              >
                <mark.icon className="size-3.5" aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-semibold">
                  {item.tone === "block" && (
                    <span className="text-bank-attention-ink">Blocks publishing: </span>
                  )}
                  {item.title}
                </p>
                {item.detail && <p className="text-bank-muted text-[12.5px]">{item.detail}</p>}
              </div>
              {item.fix && (
                <Button size="sm" onClick={() => onGo(item.fix as StepKey)}>
                  Fix in {STEP_TITLE[item.fix]}
                </Button>
              )}
            </li>
          );
        })}
        <li className="bg-bank-surface-2 text-bank-muted border-bank-border flex items-start gap-2.5 rounded-b-lg border-t px-4 py-2.5 text-[12.5px]">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          Only a technical round that cannot be filled stops publishing. Warnings don't — they can be fixed
          after it is live.
        </li>
      </ul>
    </Panel>
  );
}

function Audience({ jd, onGo }: { jd: JDDetail; onGo: (step: StepKey) => void }) {
  const members = jd.jd.audience_members ?? [];
  const teams = jd.jd.audience_teams ?? [];
  return (
    <Panel>
      <PanelHeader
        title="Who will see it"
        actions={
          <button
            type="button"
            className="text-bank-accent-ink text-[12.5px] hover:underline"
            onClick={() => onGo("details")}
          >
            Change in Details
          </button>
        }
      />
      <dl className="grid grid-cols-[6rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 p-4 text-[13px]">
        <dt className="text-bank-muted">Who</dt>
        <dd>
          {audienceOf(jd.jd)}
          {(members.length > 0 || teams.length > 0) && (
            <span className="text-bank-muted ml-1.5 font-mono text-[11px]">
              {[...members, ...teams.map((t) => `team ${t}`)].join(", ")}
            </span>
          )}
        </dd>
        <dt className="text-bank-muted">Client</dt>
        <dd>
          {!jd.jd.client_name
            ? "None named"
            : jd.jd.show_client
              ? `${jd.jd.client_name}, shown to members`
              : "Hidden — members see only the title"}
        </dd>
        <dt className="text-bank-muted">Until</dt>
        <dd>{jd.jd.expires_at ? formatDate(jd.jd.expires_at) : "No end date"}</dd>
      </dl>
    </Panel>
  );
}

function PublishCard({ jd, canManage, dirty }: { jd: JDDetail; canManage: boolean; dirty: boolean }) {
  const move = useMoveJD(jd.jd.id);
  const status = jd.jd.status;
  const block = publishBlock(jd, dirty);
  if (!canManage) return null;
  const go = (to: "active" | "in_review" | "discarded" | "archived") => move.mutate({ status: to });

  return (
    <Panel className={status === "active" ? "border-bank-good" : "border-bank-accent"}>
      <div className="flex flex-col gap-3 p-4">
        {status === "in_review" && (
          <p className="text-[13.5px]">
            Publishing puts this JD on ipp-platform's start form for {audienceOf(jd.jd)}.{" "}
            <span className="text-bank-muted">
              You can keep adding questions afterwards — they apply from the next interview. Archive takes it
              off again.
            </span>
          </p>
        )}
        {status === "active" && (
          <p className="text-[13.5px]">
            <strong className="text-bank-good-ink">Live</strong> since {formatDate(jd.jd.published_at)}
            {jd.published_by ? ` · published by ${jd.published_by}` : ""}.{" "}
            <span className="text-bank-muted">Saved changes reach members from their next interview.</span>
          </p>
        )}
        {status === "archived" && (
          <p className="text-[13.5px]">Archived — off the start form. Publish it again to bring it back.</p>
        )}
        {status === "discarded" && (
          <p className="text-[13.5px]">
            Discarded{jd.jd.discard_reason ? `: “${jd.jd.discard_reason}”` : ""}. Keep it to review it like
            any other position.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2.5">
          {(status === "in_review" || status === "archived") && (
            <Button
              variant="primary"
              className="h-10 px-4.5 text-sm"
              disabled={move.isPending || block !== null}
              onClick={() => go("active")}
            >
              {status === "archived" ? "Publish again" : "Publish"}
            </Button>
          )}
          {status === "in_review" && (
            <Button variant="ghost" disabled={move.isPending || dirty} onClick={() => go("discarded")}>
              Discard this position instead
            </Button>
          )}
          {status === "active" && (
            <Button disabled={move.isPending || dirty} onClick={() => go("archived")}>
              Archive
            </Button>
          )}
          {status === "archived" && (
            <Button disabled={move.isPending || dirty} onClick={() => go("in_review")}>
              Back to review
            </Button>
          )}
          {status === "discarded" && (
            <Button variant="primary" disabled={move.isPending || dirty} onClick={() => go("in_review")}>
              Keep it
            </Button>
          )}
          {block && (status === "in_review" || status === "archived") && (
            <span className="text-bank-attention-ink text-[12.5px]">{block}</span>
          )}
        </div>
        <ErrorNote error={move.error} />
      </div>
    </Panel>
  );
}

export function PublishStep({
  jd,
  canManage,
  dirty,
  onGo,
}: {
  jd: JDDetail;
  canManage: boolean;
  dirty: boolean;
  onGo: (step: StepKey) => void;
}) {
  return (
    <div className="flex flex-wrap items-start gap-3.5">
      <div className="flex max-w-[540px] min-w-0 flex-[1_1_360px] flex-col gap-3">
        <Checklist jd={jd} onGo={onGo} />
        <Audience jd={jd} onGo={onGo} />
        <PublishCard jd={jd} canManage={canManage} dirty={dirty} />
      </div>
      <TryAPick jd={jd} />
    </div>
  );
}
